import './App.css';

import { HashRouter, Navigate, Route, Routes } from 'react-router-dom';
import { HostApiLocal, HostOfficialServer, _smallScreen, getJwtObj } from './SharedCommon/utils';
import { IBrowserInfo, detectBrowser } from './utils/browserDetector';
import React, { Suspense, lazy } from 'react';
import { Variant, getRuntimeVariant } from './SharedCommon/variant';

import { BlockedBrowserWarning } from './components/BrowserWarning';
import GTAppSocket from './dataProvider/appSocket';
import Header from './components/Header';
import DebugMenu from './components/DebugMenu';
import { IAppSessionSettings } from './dataProvider/IAppSessionSettings';
import Loading from './components/common/Loading';
import Notify from './components/Notify';
import PwaInstallDialog from './components/PwaInstallDialog';
import { PromptPickGroup } from './components/Dialogues';
import ProtectRoute from './components/common/ProtectRoute';
import { SearchContext } from './ISearchContextType';
import { ShowLoadingContext } from './ShowLoadingContext';
import { ShowSearchBarContext } from './ShowSearchBarContext';
import Spinner from './components/Spinner';
import Welcome from './components/Welcome';
import i18n from './i18n';
import { _dataProvider } from './dataProvider/DataProvider';
import { getOrgIdFromUrl } from './SharedCommon/OrgList';
import { openMeeting } from './SharedCommon/Meeting';

const DiscoverStudy = lazy(() => import('./components/DiscoverStudy'));
const WritingBooks = lazy(() => import('./components/developClass/WritingBooks'));
const MgmtOrgGroupList = lazy(() => import('./components/MgmtOrgGroupList'));
const IdigestChatPanel = lazy(() => import('./idigest-chat/IdigestChatPanel'));
const AppDownload = lazy(() => import('./components/AppDownload'));
const AboutUs = lazy(() => import('./components/AboutUs'));
const Share2 = lazy(() => import('./components/Share2'));
const Join = lazy(() => import('./components/Join'));
const PubLesson = lazy(() => import('./components/PubLesson'));
const ClassRegistration = lazy(() => import('./components/ClassRegistration'));
const ClassManagement = lazy(() => import('./components/ClassManagement'));
const DeleteUser = lazy(() => import('./components/DeleteUser'));
const MySettings = lazy(() => import('./components/MySettings'));
const ProgramCreate = lazy(() => import('./components/developChannel/ProgramCreate'));
const ProgramDetail = lazy(() => import('./components/developChannel/ProgramDetail'));
const ChannelManage = lazy(() => import('./components/developChannel/ChannelManage'));
interface IAppState {
  headButtonIdx: number;
  signInState: boolean;
  HeadClickCount: number;
  useEditor: number;
  pickGroup4Online: boolean;
  file: any;
  msg: string;
  searchQuery: any;
  showSearchBar: boolean;
  loadingText: string;
  showLoading: boolean;
  placeholder: string; // search bar placeholder
  messageCount: number;
  browserInfo: IBrowserInfo;
}

class App extends React.Component<object, IAppState> {
  private serverUrl: string;
  private loginUrl: string;
  private settings: IAppSessionSettings;
  private _meetingWindow: any;
  private _meetingGroupId: number;
  private _orgId: number;
  private _handleLanguageChange: (lng: string) => void;
  private gtAppSocket: GTAppSocket;

  constructor(props: Record<string, never>) {
    super(props);

    this.gtAppSocket = new GTAppSocket();
    this._orgId = getOrgIdFromUrl();
    this._meetingWindow = null;
    this._meetingGroupId = -1;

    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('localapi') === '1') {
      this.serverUrl = HostApiLocal;
    } else {
      this.serverUrl = HostOfficialServer;
    }
    this.loginUrl = this.serverUrl + '/user/login';

    let lastSettings = localStorage.getItem('iDigestSettings');
    console.log('lastSettings from storage = ', lastSettings, typeof lastSettings);
    if (!lastSettings || lastSettings.charAt(0) !== '{') {
      lastSettings = '{"headButtonIdx":1, "sessionIdx":0, "name":"", "chapterIdx":-1}';
    }
    this.settings = JSON.parse(lastSettings);
    if (this.settings.lang) {
      i18n.changeLanguage(this.settings.lang);
    }
    this._handleLanguageChange = (lng: string) => {
      this._saveSettings({ lang: lng } as IAppSessionSettings);
    };
    i18n.on('languageChanged', this._handleLanguageChange);

    const jwtObj = getJwtObj();
    console.log('signInState at start: ', jwtObj);

    // Reopen the last main tab. Study resumes its own book/chapter; fellowship and develop open at their top level.
    // Only applies when launched without an explicit route, so shared links are untouched.
    let startButtonIdx = 1;
    if (getRuntimeVariant() === Variant.idigest && /^#?\/?$/.test(window.location.hash)) {
      const tabs: Record<string, number> = { group: 2, develop: -1 };
      const { mainTab } = this.settings;
      // Same rule as the header's Authoring link; WritingBooks redirects home when it fails.
      const targetOrg = jwtObj.organizations?.find((org: { id: number; role: number }) => org.id === this._orgId);
      const role = targetOrg?.role ?? 0;
      const canDevelop = !_smallScreen && (!this._orgId || role === 1 || role === 2);
      if (mainTab === 'develop' && !canDevelop) {
        this.settings.mainTab = 'study';
        localStorage.setItem('iDigestSettings', JSON.stringify(this.settings));
      } else if (mainTab && tabs[mainTab] !== undefined) {
        window.location.hash = `#/${mainTab}`;
        startButtonIdx = tabs[mainTab];
      }
    }

    this.state = {
      headButtonIdx: startButtonIdx,
      HeadClickCount: 0,
      signInState: jwtObj.userId !== -1,
      useEditor: parseInt(urlParams.get('editor') || ''),
      pickGroup4Online: false,
      file: null,
      msg: '',
      searchQuery: '',
      showSearchBar: true,
      showLoading: false,
      loadingText: '',
      placeholder: '',
      messageCount: 0,
      browserInfo: detectBrowser()
    };

    _dataProvider.init(this.serverUrl, this._orgId);

    if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        console.log('message listener event:', event.data);
        this.setState({ file: event.data.file, msg: event.data.msg });
      });
    }

    console.log('the state.signInState, useEditor', this.state.signInState, this.state.useEditor);
  }

  updateSearchQuery = (newQuery: string) => {
    this.setState({ searchQuery: newQuery });
  };

  setSearchBarVisibility = (b: boolean) => {
    this.setState({ showSearchBar: b, searchQuery: '' });
  };
  setLoadingVisibility = (showLoading: boolean, loadingText?: string) => {
    this.setState({ showLoading, loadingText: loadingText || '' });
  };
  setPlaceholder = (s: string) => {
    this.setState({ placeholder: s, searchQuery: '' });
  };

  private onNewMessage = (data: any) => {
    console.log('App socket onNewMessage: ', data);
    const href = window.location.href;
    if (!href.includes('/#/group') && !href.includes('/#/breakout-room-mgnt')) {
      this.setState({ messageCount: this.state.messageCount + 1 });
    } else {
      this.setState({ messageCount: 0 });
    }
  };

  // Some launchers (e.g. opening a shared link on Chromebook) navigate to the bare origin first
  // and only append the hash afterwards, so the lesson-route check in render() can miss it on
  // the very first paint. Re-render when the hash changes so the app picks it up without
  // requiring a manual reload.
  private _handleUrlChange = (): void => {
    this._rememberMainTab();
    this.forceUpdate();
  };

  public componentDidMount() {
    console.log('Mounted');
    this.gtAppSocket.init({ onNewMessage: this.onNewMessage });
    window.addEventListener('hashchange', this._handleUrlChange);
    this._rememberMainTab();
  }

  public componentWillUnmount() {
    window.removeEventListener('hashchange', this._handleUrlChange);
    i18n.off('languageChanged', this._handleLanguageChange);
  }

  private _rememberMainTab = (): void => {
    if (getRuntimeVariant() !== Variant.idigest) {
      return;
    }
    const path = window.location.hash.replace(/^#\/?/, '').split(/[/?]/)[0];
    const mainTab = path === '' ? 'study' : path === 'group' || path === 'develop' ? path : undefined;
    if (mainTab && mainTab !== this.settings.mainTab) {
      this._saveSettings({ mainTab } as IAppSessionSettings);
    }
  };

  private _signInOut = (signInState: boolean): void => {
    console.log('Sign-in state changed in App:', signInState);

    if (!signInState) {
      const lang = this.settings.lang;
      this.settings = { headButtonIdx: 1, sessionIdx: 0, name: '', chapterIdx: -1, lang };
      localStorage.setItem('iDigestSettings', JSON.stringify(this.settings));
      _dataProvider.reset();
      this.setState({ messageCount: 0 });
    } else {
      this.gtAppSocket.init({ onNewMessage: this.onNewMessage });
    }

    this.setState({ signInState });
  };

  public render(): React.ReactNode {
    console.log('Rendering App, signInState:', this.state.signInState);

    // 浏览器检测：如果是被禁止的浏览器，显示警告页面
    if (this.state.browserInfo.isBlocked) {
      return (
        <BlockedBrowserWarning browserName={this.state.browserInfo.name} isMobile={this.state.browserInfo.isMobile} />
      );
    }

    if (window.location.href.indexOf('programDetail') !== -1) {
      return (
        <HashRouter>
          <Routes>
            <Route path='/programDetail' element={<ProgramDetail />}></Route>
          </Routes>
        </HashRouter>
      );
    }
    if (window.location.href.indexOf('lesson') !== -1) {
      return (
        <HashRouter>
          <Suspense fallback={<Spinner label='Loading...' />}>
            <Routes>
              <Route path='/lesson/:id/:file' element={<PubLesson />}></Route>
            </Routes>
          </Suspense>
        </HashRouter>
      );
    }

    return (
      <SearchContext.Provider
        value={{
          searchQuery: this.state.searchQuery,
          updateSearchQuery: this.updateSearchQuery
        }}>
        <ShowLoadingContext.Provider
          value={{
            showLoading: this.state.showLoading,
            setShowLoading: this.setLoadingVisibility
          }}>
          <ShowSearchBarContext.Provider
            value={{
              showSearchBar: this.state.showSearchBar,
              setVisible: this.setSearchBarVisibility
            }}>
            <Notify count={this.state.messageCount} />
            {process.env.NODE_ENV === 'development' && <DebugMenu />}
            <PwaInstallDialog />
            <HashRouter>
              <div id='idHeaderMenuBar' className='headerDiv'>
                <Header
                  buttonIdx={this.state.headButtonIdx}
                  signInOut={this._signInOut}
                  setClickCount={this._setClickCount}
                  loginUrl={this.loginUrl}
                  signInState={this.state.signInState}
                  preview={window.location.href.indexOf('preview') !== -1}
                  getOnline={this._getOnline}
                />
              </div>
              {this.state.showLoading && (
                <div className='loading-page'>
                  <Loading label={this.state.loadingText} />
                </div>
              )}
              <Suspense fallback={<Spinner label='Loading...' />}>
                <Routes>
                  {getRuntimeVariant() === Variant.fishmeet && (
                    <Route path='/' element={<Navigate to='/breakout-room-mgnt' replace />} />
                  )}
                  <Route
                    path={getRuntimeVariant() === Variant.idigest ? '/' : '/discover-study'}
                    element={
                      <DiscoverStudy
                        signInState={this.state.signInState}
                        HeadClickCount={this.state.HeadClickCount}
                        saveSettings={this._saveSettings}
                        settings={this.settings}
                      />
                    }></Route>
                  <Route path='/share' element={<Share2 file={this.state.file} msg={this.state.msg} />}></Route>
                  <Route path='/join' element={<Join signInState={this.state.signInState} />}></Route>
                  <Route path='/AboutUs' element={<AboutUs />}></Route>
                  <Route
                    path='/group'
                    element={
                      <IdigestChatPanel
                        signInState={this.state.signInState}
                        headClickCount={this.state.HeadClickCount}
                      />
                    }
                  ></Route>
                  <Route
                    path='/org'
                    element={
                      <MgmtOrgGroupList
                        HeadClickCount={this.state.HeadClickCount}
                        signInState={this.state.signInState}
                        saveSettings={this._saveSettings}
                        settings={this.settings}
                        headerHeight={52}
                      />
                    }></Route>
                  <Route
                    path='/develop'
                    element={
                      <WritingBooks
                        HeadClickCount={this.state.HeadClickCount}
                        signInState={this.state.signInState}
                        saveSettings={this._saveSettings}
                        settings={this.settings}
                        useEditor={this.state.useEditor}
                        preview={window.location.href.indexOf('preview') !== -1}
                      />
                    }></Route>
                  <Route path='/channelManage' element={<ChannelManage />} />
                  <Route path='/programCreate' element={<ProgramCreate />} />
                  <Route path='/programDetail' element={<ProgramDetail />} />
                  <Route
                    path='/appDownload'
                    element={
                      <AppDownload
                        signInState={this.state.signInState}
                        HeadClickCount={this.state.HeadClickCount}
                        saveSettings={this._saveSettings}
                        settings={this.settings}
                      />
                    }></Route>
                  <Route
                    path='/classregister/:trainingId/:joinToken'
                    element={
                      <ClassRegistration
                        signInState={this.state.signInState}
                        contentHeight='100vh'
                        onSignInOut={this._signInOut}
                      />
                    }
                  />
                  <Route path='/my-settings' element={<MySettings />} />
                  <Route path='/classmanagement' element={<ClassManagement />} />
                  <Route path='/userDeletion' element={<DeleteUser />} />
                  <Route
                    path='/breakout-room-mgnt'
                    element={
                      <ProtectRoute signInState={this.state.signInState}>
                        <IdigestChatPanel
                          signInState={this.state.signInState}
                          headClickCount={this.state.HeadClickCount}
                          notesDataId='breakout-room-notes,0'
                        />
                      </ProtectRoute>
                    }></Route>
                  <Route path='/welcome' element={<Welcome signInOut={this._signInOut} />}></Route>
                </Routes>
              </Suspense>
              {this.state.pickGroup4Online && <PromptPickGroup pickGroup={this._pickGroup} />}
            </HashRouter>
          </ShowSearchBarContext.Provider>
        </ShowLoadingContext.Provider>
      </SearchContext.Provider>
    );
  }

  private _setClickCount = (headButtonIdx: number) => {
    console.log('_setClickCount: ', this.state.searchQuery);
    this.settings.headButtonIdx = headButtonIdx;
    // In-app tab navigation doesn't fire 'hashchange', so remember the main tab from the header click.
    const mainTab = ({ 1: 'study', 2: 'group', [-1]: 'develop' } as Record<number, string>)[headButtonIdx];
    if (mainTab && getRuntimeVariant() === Variant.idigest) {
      this._saveSettings({ mainTab } as IAppSessionSettings);
    }
    const HeadClickCount = this.state.HeadClickCount + 1;
    this.setState({ HeadClickCount, headButtonIdx, searchQuery: '', messageCount: 0 });
  };

  private _saveSettings = (settings?: IAppSessionSettings) => {
    console.log('_saveSettings 1:', settings);
    if (settings) {
      this.settings = { ...this.settings, ...settings };
    }
    console.log('_saveSettings 2: ', this.settings);
    localStorage.setItem('iDigestSettings', JSON.stringify(this.settings));
  };

  private _getOnline = () => {
    this.setState({ pickGroup4Online: true });
  };

  private _pickGroup = async (id: number, extra?: { enablePreBreakout?: boolean }): Promise<void | boolean> => {
    const { enablePreBreakout } = extra ?? {};
    this.setState({ pickGroup4Online: false });
    if (!id) {
      return;
    }

    try {
      await openMeeting({
        groupId: id,
        enablePreBreakout
      });

      return true;
    } catch (err) {
      console.log('Error opening meeting window:', err);
    }
    return;
  };

}

export default App;
